import _ from 'lodash';

import { getRenderedChartSort } from './canvasMapping/legacyDefaults';
import { GROUP_LIMIT } from './chartDefinitionParts';
import { hasTermDimension } from './dimensions';
import { guessDisplayOptions } from './chartDisplayOptions';
import { getResolvedMetricIds } from './seriesVariants';
import { isTimelineHeatmap, isTimelineHeatmapDef } from './visTypes';

export type IntervalType = Studio.ChartInterval['type'];

const ALL_INTERVAL_TYPES: IntervalType[] = [
  'calendar',
  'fiscal',
  'fixedDays',
  'hourOfDay',
];

// Stacked charts bucket dates as the matrix's termed histogram, which only
// takes a calendar or fixed-day interval
const STACKED_INTERVAL_TYPES: IntervalType[] = ['calendar', 'fixedDays'];

export interface ChartCapabilities {
  // Charts with a date axis: the calendar interval they default to and the
  // kinds of interval they can take
  interval?: { defaultUnit: FleetOps.Interval; types: IntervalType[] };
  canSort: boolean;
  canLimit: boolean;
  // Split Lines take a limit alone, without the limit mode
  isSplitLine: boolean;
  // The metrics a grouped chart can be sorted by: the ones it draws
  sortMetricIds: string[];
  // How many terms it shows without a limit chosen: a ranking (a Bar of
  // terms) its top 10, everything else all of them
  defaultLimit: Studio.TableLimit;
}

const NONE: ChartCapabilities = {
  canSort: false,
  canLimit: false,
  isSplitLine: false,
  sortMetricIds: [],
  defaultLimit: { mode: 'all' },
};

// What a chart's shape lets it use of its ChartOptions. Mirrors the shapes
// buildChartDefinition builds; Tables have their own (TableOptions).
export const getChartCapabilities = (
  content: Pick<
    Studio.ChartContent,
    | 'visType'
    | 'metricIds'
    | 'seriesVariants'
    | 'dimensions'
    | 'dimensionOptions'
  > &
    Partial<Pick<Studio.ChartContent, 'chart'>>,
): ChartCapabilities => {
  // Sorted by metrics, a variant's by its metric (see seriesVariants)
  const metricIds = getResolvedMetricIds(content);
  const seriesCount = (content.metricIds ?? []).length;
  const [dimA, dimB] = content.dimensions ?? [];
  // Ranges, days and months have no terms to sort or limit
  const hasTerms = hasTermDimension(content);
  const overTime: ChartCapabilities = {
    ...NONE,
    interval: { defaultUnit: 'auto', types: ALL_INTERVAL_TYPES },
  };
  const grouped = (sortMetricIds: string[]): ChartCapabilities =>
    hasTerms ? { ...NONE, canSort: true, canLimit: true, sortMetricIds } : NONE;
  const stackedByDate = (): ChartCapabilities => ({
    ...grouped(metricIds.slice(0, 1)),
    ...(dimA
      ? { interval: { defaultUnit: 'month', types: STACKED_INTERVAL_TYPES } }
      : {}),
  });

  switch (content.visType) {
    case 'line':
      // Splitting only works with a single metric, whose lines can be
      // ranked by it
      return dimA && seriesCount === 1
        ? {
            ...overTime,
            canSort: true,
            canLimit: true,
            isSplitLine: true,
            sortMetricIds: metricIds.slice(0, 1),
          }
        : overTime;
    case 'bar':
    case 'pictorialBar':
      // A ranking: its top 10 unless more are asked for
      return dimA
        ? {
            ...grouped(metricIds),
            ...(hasTerms ? { defaultLimit: { mode: 'top' } } : {}),
          }
        : overTime;
    case 'combo':
      return dimA ? grouped(metricIds) : overTime;
    case 'pie':
      return grouped(metricIds.slice(0, 1));
    case 'radar':
      return grouped(metricIds);
    case 'heatmap':
      // A Timeline's columns are dates; nothing to sort or limit
      if (isTimelineHeatmap(content)) {
        return overTime;
      }
      return dimB ? grouped(metricIds.slice(0, 1)) : NONE;
    case 'stackedBar':
      return dimB ? grouped(metricIds.slice(0, 1)) : stackedByDate();
    case 'area':
      return stackedByDate();
    case 'scatter':
      // x, y and size
      return grouped(metricIds.slice(0, 3));
    case 'treeMap':
    case 'waterfall':
      return dimA ? grouped(metricIds.slice(0, 1)) : overTime;
    case 'packedBubble':
    case 'funnel':
      return grouped(metricIds.slice(0, 1));
    case 'regionMap':
      // Every term is shown, in its place on the map
      return NONE;
    case 'bullet':
      // A bullet per term of the first metric, or one per metric
      return dimA ? grouped(metricIds.slice(0, 1)) : NONE;
    case 'sankey':
      // A limit on the flows' second dimension; nothing to sort by
      return dimA && dimB ? { ...NONE, canLimit: true } : NONE;
    default:
      return NONE;
  }
};

// The chart's interval, when it can take the chosen one
export const getChartInterval = ({
  options,
  capabilities,
}: {
  options: Studio.ChartOptions;
  capabilities: ChartCapabilities;
}): Studio.ChartInterval | undefined => {
  const { interval } = options;
  return interval &&
    capabilities.interval &&
    capabilities.interval.types.includes(interval.type)
    ? interval
    : undefined;
};

// Hour-of-day buckets aren't a date axis, so they can't be forecast
export const isHourOfDay = (
  content: Pick<
    Studio.ChartContent,
    'visType' | 'metricIds' | 'dimensions' | 'chart'
  >,
): boolean =>
  getChartInterval({
    options: content.chart ?? {},
    capabilities: getChartCapabilities(content),
  })?.type === 'hourOfDay';

// The date axis's interval settings. Absent is the vis type's default
// calendar interval.
export const buildIntervalParts = ({
  options,
  capabilities,
}: {
  options: Studio.ChartOptions;
  capabilities: ChartCapabilities;
}): Partial<V5ChartDefinition> => {
  if (!capabilities.interval) {
    return {};
  }
  const interval = getChartInterval({ options, capabilities });
  const partial: Partial<V5ChartDefinition> = options.excludePartialIntervals
    ? { excludePartialIntervals: true }
    : {};
  if (!interval) {
    return {
      trendByCalendarInterval: capabilities.interval.defaultUnit,
      ...partial,
    };
  }
  switch (interval.type) {
    case 'calendar':
      return { trendByCalendarInterval: interval.unit, ...partial };
    case 'fiscal':
      return { trendByFiscalInterval: interval.unit, ...partial };
    case 'fixedDays':
      return { trendByFixedIntervalDays: interval.days, ...partial };
    case 'hourOfDay':
    default:
      return { trendByHourInterval: true, ...partial };
  }
};

// The sort a chart has when none is chosen: the first metric, descending,
// but a split Line's lines are left in the query's own order (the terms
// themselves, descending), as Studio saved them before they could be sorted
export const getDefaultChartSort = (
  capabilities: ChartCapabilities,
): Studio.ChartSort =>
  capabilities.isSplitLine
    ? { by: 'none' }
    : {
        by: 'metric',
        metricId: capabilities.sortMetricIds[0],
        direction: 'desc',
      };

// How grouped terms are sorted. Absent is the chart's default sort
// (getDefaultChartSort). A split Line's query sends no direction, which
// graphql takes as descending (buildGroupBy), so it's ranked highest first
// by its metric, or left as its terms come.
export const buildSortParts = ({
  options,
  capabilities,
  dimension,
}: {
  options: Studio.ChartOptions;
  capabilities: ChartCapabilities;
  dimension: string;
}): Partial<V5ChartDefinition> => {
  if (!capabilities.canSort) {
    return {};
  }
  const [primary] = capabilities.sortMetricIds;
  const sort: Studio.ChartSort =
    options.sort ?? getDefaultChartSort(capabilities);
  if (
    sort.by === 'none' ||
    (sort.by === 'dimension' && capabilities.isSplitLine)
  ) {
    return {};
  }
  if (sort.by === 'dimension') {
    return {
      groupBySortBy: sort.direction,
      grouping: {
        defaultSort: { field: dimension, direction: sort.direction },
      },
    };
  }
  // A metric no longer drawn falls back to the first
  const metricId = capabilities.sortMetricIds.includes(sort.metricId)
    ? sort.metricId
    : primary;
  const direction = capabilities.isSplitLine ? 'desc' : sort.direction;
  return {
    groupBySortBy: direction,
    grouping: {
      defaultSort: { field: metricId, metricId, direction },
    },
  };
};

// How many terms are shown. Absent is the chart's default (a ranking's top
// 10, else all of them); the top N defaults to 10.
export const buildLimitParts = ({
  options,
  capabilities,
}: {
  options: Studio.ChartOptions;
  capabilities: ChartCapabilities;
}): Partial<V5ChartDefinition> => {
  if (!capabilities.canLimit) {
    return {};
  }
  const limit = options.limit ?? capabilities.defaultLimit;
  if (limit.mode === 'all') {
    return capabilities.isSplitLine ? {} : { groupByLimitMode: 'show all' };
  }
  const n = limit.n ?? GROUP_LIMIT;
  return capabilities.isSplitLine
    ? { groupByLimit: n }
    : { groupByLimit: n, groupByLimitMode: 'top n' };
};

// Without undefined values; undefined when nothing is left
const compact = <T extends object>(value: T): T | undefined => {
  const compacted = _.omitBy(value, _.isUndefined);
  return _.isEmpty(compacted) ? undefined : (compacted as T);
};

const guessInterval = ({
  chartDef,
  capabilities,
}: {
  chartDef: V5ChartDefinition;
  capabilities: ChartCapabilities;
}): Studio.ChartInterval | undefined => {
  if (!capabilities.interval) {
    return undefined;
  }
  // In the order the classic editor reads them
  if (chartDef.trendByFixedIntervalDays) {
    return { type: 'fixedDays', days: chartDef.trendByFixedIntervalDays };
  }
  if (chartDef.trendByHourInterval) {
    return { type: 'hourOfDay' };
  }
  if (chartDef.trendByFiscalInterval) {
    return { type: 'fiscal', unit: chartDef.trendByFiscalInterval };
  }
  const unit = chartDef.trendByCalendarInterval;
  return unit && unit !== capabilities.interval.defaultUnit
    ? { type: 'calendar', unit }
    : undefined;
};

const guessSort = ({
  chartDef,
  capabilities,
  dimension,
}: {
  chartDef: V5ChartDefinition;
  capabilities: ChartCapabilities;
  dimension: string | undefined;
}): Studio.ChartSort | undefined => {
  if (!capabilities.canSort) {
    return undefined;
  }
  // The chart's default sort is saved as none
  const unlessDefault = (sort: Studio.ChartSort) =>
    _.isEqual(sort, getDefaultChartSort(capabilities)) ? undefined : sort;
  // Charts the classic editor saved without a sort keep the query's own
  // order (which sorts the terms themselves, descending)
  const isUnsorted =
    !chartDef.groupBySortBy &&
    !chartDef.grouping?.defaultSort &&
    !chartDef.series.some((s) => s.sortByMetricId);
  if (isUnsorted) {
    return unlessDefault({ by: 'none' });
  }
  // Read as the chart's query sorts, not as it's saved (see
  // getRenderedChartSort)
  const rendered = getRenderedChartSort(chartDef);
  if (!rendered) {
    return undefined;
  }
  const { direction, metricId, field } = rendered;
  if (metricId) {
    // One Studio can't sort by is left to the default, and so opens as
    // Classic
    if (!capabilities.sortMetricIds.includes(metricId)) {
      return undefined;
    }
    return unlessDefault({ by: 'metric', metricId, direction });
  }
  if (field !== dimension) {
    return undefined;
  }
  // A split Line's terms, descending, are its lines left unsorted
  return capabilities.isSplitLine && direction === 'desc'
    ? undefined
    : { by: 'dimension', direction };
};

const guessLimit = ({
  chartDef,
  capabilities,
}: {
  chartDef: V5ChartDefinition;
  capabilities: ChartCapabilities;
}): Studio.TableLimit | undefined => {
  if (!capabilities.canLimit) {
    return undefined;
  }
  const { groupByLimit, groupByLimitMode } = chartDef;
  // Without a limit every term is shown
  const limit: Studio.TableLimit =
    groupByLimit === undefined
      ? groupByLimitMode === 'top n'
        ? { mode: 'top' }
        : { mode: 'all' }
      : groupByLimit === GROUP_LIMIT
        ? { mode: 'top' }
        : { mode: 'top', n: groupByLimit };
  return _.isEqual(limit, capabilities.defaultLimit) ? undefined : limit;
};

// The chart options closest to a chart definition (display options too):
// only those that differ from Studio's defaults, so a chart Studio built
// guesses back to none. Whether they reproduce it is checked separately.
export const guessChartOptions = ({
  chartDef,
  content,
}: {
  chartDef: V5ChartDefinition;
  content: Pick<
    Studio.ChartContent,
    | 'visType'
    | 'metricIds'
    | 'seriesVariants'
    | 'dimensions'
    | 'dimensionOptions'
  >;
}): Studio.ChartOptions | undefined => {
  // A heatmap over dates is a Timeline, which decides what else it takes
  const heatmapMode =
    content.visType === 'heatmap' && isTimelineHeatmapDef(chartDef)
      ? ('timeline' as const)
      : undefined;
  const modal = heatmapMode ? { ...content, chart: { heatmapMode } } : content;
  const capabilities = getChartCapabilities(modal);
  return compact({
    ...guessDisplayOptions({ chartDef, content: modal }),
    heatmapMode,
    interval: guessInterval({ chartDef, capabilities }),
    excludePartialIntervals:
      (capabilities.interval && chartDef.excludePartialIntervals) || undefined,
    sort: guessSort({
      chartDef,
      capabilities,
      dimension: (content.dimensions ?? [])[0],
    }),
    limit: guessLimit({ chartDef, capabilities }),
    // The other of a pair of gadget types (see getGadgetType)
    isDonut: chartDef.gadgetType === 'donut' || undefined,
    isSolid: (chartDef.gadgetType === 'pie' && chartDef.isSolid) || undefined,
    isHorizontal: chartDef.gadgetType === 'horizontalBar' || undefined,
    isPyramid: chartDef.gadgetType === 'pyramid' || undefined,
  });
};
